import { useState, useEffect, useMemo } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { teamsApi, usersApi } from "@/lib/api";
import { adaptApiKey, adaptUsageChart } from "@/lib/adapters";
import type { AdaptedAPIKey, AdaptedUsageChart, UsageRecord, WalletAndQuota, UsageSummaryItem } from "@/lib/types";
import {
  TrendingUp, TrendingDown, Activity, Zap,
  FlaskConical, ArrowRight, Clock, Check, X,
  BarChart3, Key, Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend,
} from "recharts";
import { useLanguage } from "@/contexts/LanguageContext";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="sci-card px-3 py-2 text-xs">
        <p className="text-slate-400 mb-1.5">{label}</p>
        {payload.map((p: any) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-400">{p.dataKey}:</span>
            <span className="text-white font-mono">{p.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { t } = useLanguage();
  const { teamId } = useAuth();
  const [chartRange, setChartRange] = useState<7 | 14 | 30>(30);
  const [chartData, setChartData] = useState<AdaptedUsageChart[]>([]);
  const [recentCalls, setRecentCalls] = useState<UsageRecord[]>([]);
  const [apiKeys, setApiKeys] = useState<AdaptedAPIKey[]>([]);
  const [wallet, setWallet] = useState<WalletAndQuota | null>(null);
  const [summaryItems, setSummaryItems] = useState<UsageSummaryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    const after = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    // Usage data is fetched via the personal /users/me endpoints so regular
    // members (who can't read team-wide usage) still see their own activity
    // here. Wallet/quota stays team-scoped since every team member can read it.
    Promise.all([
      usersApi.getMyUsageSummary({ group_by: "day", after }),
      usersApi.getMyUsage({ limit: 5 }),
      usersApi.listApiKeys(),
      teamsApi.getWallet(teamId),
    ])
      .then(([summary, usage, keys, walletData]) => {
        setChartData(adaptUsageChart(summary.items));
        setSummaryItems(summary.items);
        setRecentCalls(usage.records);
        setApiKeys(keys.map(adaptApiKey));
        setWallet(walletData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [teamId]);

  // Compute stats from real data
  const totalTokens = summaryItems.reduce((s, i) => s + i.input_tokens + i.output_tokens, 0);
  const totalCalls = summaryItems.reduce((s, i) => s + i.request_count, 0);
  const totalCost = summaryItems.reduce((s, i) => s + Number(i.total_st), 0);

  // Pad chartData with zero-value days so switching 7/14/30 always shows a
  // full window on the X axis. Backend's group_key is a UTC DATE — match
  // against YYYY-MM-DD strings, generated in UTC to avoid TZ drift.
  const visibleChartData = useMemo<AdaptedUsageChart[]>(() => {
    const byDate = new Map(chartData.map((d) => [d.date.slice(0, 10), d]));
    const out: AdaptedUsageChart[] = [];
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    for (let i = chartRange - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(d.getUTCDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push(byDate.get(key) ?? { date: key, llm: 0 });
    }
    return out;
  }, [chartData, chartRange]);

  const STAT_CARDS = [
    {
      label: t("dash.totalTokens"),
      value: totalTokens > 1_000_000 ? `${(totalTokens / 1_000_000).toFixed(1)}M` : totalTokens > 1_000 ? `${(totalTokens / 1_000).toFixed(1)}K` : String(totalTokens),
      change: "",
      up: true,
      icon: <Activity size={16} className="text-blue-400" />,
      color: "blue",
    },
    {
      label: t("dash.apiCalls"),
      value: totalCalls.toLocaleString(),
      change: "",
      up: true,
      icon: <Zap size={16} className="text-cyan-400" />,
      color: "cyan",
    },
    {
      label: t("dash.balance"),
      value: wallet ? `${Number(wallet.wallet.st_balance).toLocaleString(undefined, { maximumFractionDigits: 2 })} ST` : "—",
      change: "",
      up: true,
      icon: <TrendingUp size={16} className="text-emerald-400" />,
      color: "emerald",
    },
    {
      label: t("dash.estCost"),
      value: `${totalCost.toLocaleString()} ST`,
      change: "",
      up: true,
      icon: <BarChart3 size={16} className="text-amber-400" />,
      color: "amber",
    },
  ];

  return (
    <ConsoleLayout
      title={t("dash.title")}
      subtitle={t("dash.subtitle")}
    >
      <div className="p-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((card) => (
            <div key={card.label} className="sci-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  {card.icon}
                </div>
                <div className={cn(
                  "flex items-center gap-1 text-xs font-medium",
                  card.up ? "text-emerald-400" : "text-red-400"
                )}>
                  {card.up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {card.change}
                </div>
              </div>
              <div className="text-2xl font-bold text-white mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {card.value}
              </div>
              <div className="text-xs text-slate-500">{card.label}</div>
            </div>
          ))}
        </div>

        {/* Usage Chart */}
        <div className="sci-card p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t("dash.usageBreakdown")}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{t("dash.dailyUsage")}</p>
            </div>
            <div className="flex items-center gap-1">
              {([7, 14, 30] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setChartRange(r)}
                  className={cn(
                    "text-xs px-2.5 py-1 rounded-lg transition-all",
                    chartRange === r ? "bg-blue-500/20 text-blue-400" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  {r}d
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={visibleChartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="llmGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="circle"
                iconSize={6}
                formatter={(value) => <span style={{ color: '#94A3B8', fontSize: 11 }}>{value}</span>}
              />
              <Area type="monotone" dataKey="llm" name="Token Usage" stroke="#3B82F6" fill="url(#llmGrad)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Recent API Calls */}
          <div className="sci-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t("dash.recentCalls")}
              </h3>
              <Link href="/console/logs">
                <button className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  {t("dash.viewAll")} <ArrowRight size={11} />
                </button>
              </Link>
            </div>
            <div className="space-y-2">
              {recentCalls.length === 0 && !loading && (
                <div className="text-xs text-slate-500 py-4 text-center">No recent calls</div>
              )}
              {recentCalls.map((call) => (
                <div key={call.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0",
                    call.status === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                  )}>
                    {call.status === "success" ? <Check size={10} /> : <X size={10} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-300 font-mono truncate">{call.science_llm_key || call.model_key || "—"}</div>
                    <div className="text-[10px] text-slate-600">{new Date(call.started_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    {call.total_tokens > 0 && (
                      <div className="text-xs font-mono text-slate-400">{call.total_tokens.toLocaleString()} tok</div>
                    )}
                    <div className="text-[10px] text-slate-600">
                      {(call.latency_ms ?? 0) > 0 ? `${((call.latency_ms ?? 0) / 1000).toFixed(1)}s` : "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Access */}
          <div className="space-y-4">
            {/* Active Keys */}
            <div className="sci-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {t("dash.activeKeys")}
                </h3>
                <Link href="/console/keys">
                  <button className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    {t("dash.manage")} <ArrowRight size={11} />
                  </button>
                </Link>
              </div>
              <div className="space-y-2">
                {apiKeys.filter(k => k.status === "active").slice(0, 3).map((key) => (
                  <div key={key.id} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <Key size={10} className="text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-300 truncate">{key.name}</div>
                      <div className="text-[10px] font-mono text-slate-600">{key.key}</div>
                    </div>
                    <div className="text-[10px] text-slate-500 text-right">
                      {key.lastUsed}
                    </div>
                  </div>
                ))}
                {apiKeys.filter(k => k.status === "active").length === 0 && !loading && (
                  <div className="text-xs text-slate-500 py-2 text-center">No active keys</div>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="sci-card p-5">
              <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t("dash.quickAccess")}
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { label: "Science LLMs", href: "/console/llms", icon: <FlaskConical size={13} className="text-blue-400" /> },
                ].map((item) => (
                  <Link key={item.href} href={item.href}>
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white/3 border border-white/8 hover:border-white/15 transition-all cursor-pointer">
                      {item.icon}
                      <span className="text-xs text-slate-300">{item.label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ConsoleLayout>
  );
}
